import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Box, ButtonGroup, Button, IconButton, InputAdornment, Link, List, Paper, Stack, TextField, Typography } from '@mui/material';
import { FormatShapesOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Styles from '../../common/styles/styles';
import { validateFirstName, validateLastName, validateUserName, validatePhoneNumber, validateEmailAddr, validatePassword, validateConfirmPassword } from '../../common/utils/validationUtil';
import { capFirstLetter, formatPhoneNo } from '../../common/utils/massageDataUtil';

export default function RegistrationModal() {
  
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();  
  let isFormValid = Object.values(errors).filter(value => value !=='').length > 0;

  const handleOpen = () => setOpen(true);
  
  const handleClose = () => {
    setOpen(false);
    setFormData({ ...formData, firstname: '', lastname: '', emailaddr: '', username: '', phone: '', groupname: 'Engineer', cli: false, disabled: false, password: '', vPassword: ''});
    setErrors({});
    if (urlPath.includes('register')) {
      navigate('/'); 
    }
  }

  const urlPath = window.location.pathname;

  const [showPasswords, setShowPasswords] = useState({
    password: false,
    vPassword: false
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    username: '',
    phone: '',
    emailaddr: '',        
    password: '',
    vPassword: '',
    groupname: 'Engineer',
    cli: false,
    disabled: false
  })

  const handleInputChange = (field) => (e) => {
    
    let value = e.target.value;
    if ( field === 'firstname' || field === 'lastname' ) {
      value = capFirstLetter(value);
    } else if ( field === 'phone' ) {
      value = formatPhoneNo(value, e);
    }
    
    setFormData({ ...formData, [field]: value });  
    const newErrors = { ...errors };
  
    if (field === 'firstname') newErrors.firstname = validateFirstName(value);
    if (field === 'lastname') newErrors.lastname = validateLastName(value);
    if (field === 'username') newErrors.username = validateUserName(value);
    if (field === 'phone') newErrors.phone = validatePhoneNumber(value);
    if (field === 'emailaddr') newErrors.emailaddr = validateEmailAddr(value);
    if (field === 'password') newErrors.password = validatePassword(value);
    if (field === 'vPassword') newErrors.vPassword = validateConfirmPassword(formData.password, value);
  
    setErrors(newErrors);
  }

  const title = 'Register New User';

  const registerUser = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API}/users/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();
        if (result.message === 'success') {
          toast.success('Registration Successful: Please Log In to Verify Your Account.', { closeOnClick: true, autoClose: 10000 });
          handleClose();
        } else {
          toast.error(result.message);
        }
    } catch (error) {
      console.error('Network error.  Please try again: ', error);
    }
  }
  
  const handleRegistration = (event) => {
    event.preventDefault();
    event.stopPropagation();
    registerUser();
  }

  const textFieldStyles = Styles.textFieldStyles;

  useEffect(() => {
      if ( urlPath.includes('register') ) {
        setOpen(true);
      }
  }, [open]);

  return (
    <>
        <Link 
          sx={{ 
            ml: 5,
            outline: 'none',
            '&:focus': {
              outline: 'none'
            }
          }} 
          onClick={handleOpen} 
          style={{ cursor: 'pointer' }} 
          underline="hover" 
          color="#c1d4e7"
        >
          {title}
        </Link>
        <Modal 
          open={open} 
          onClose={handleClose}
          sx={{
            '&:focus': {
              outline: 'none'
            },
            '& .MuiModal-backdrop': {
              outline: 'none'
            }
          }}
        >
            <Box sx={{color: 'white', p: 4, outline: 'none', '&:focus': { outline: 'none' }}}>
                <Box
                  component={Paper}
                  elevation={3}
                  sx={{
                    mx: 'auto',
                    position: 'absolute',
                    // top: '55%',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    // width: '90vw',
                    // height: '80vh', 
                    height: '80%', 
                    // maxHeight: '80vh',
                    width: '85%', 
                    maxWidth: '400px',
                    overflowY: 'auto', 
                    bgcolor: '#1e1e1e',
                    p: 2,
                    outline: 'none',
                    '&:focus': {
                      outline: 'none'
                    },
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: '#1e1e1e',
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: '#FFF',
                      borderRadius: "4px",
                    },
                  }}
                >
                <Typography variant="h4" component="h2" color="white" mb={2}>
                  {title}
                </Typography>
                <Box component="form" onSubmit={handleRegistration}>
                    <Stack>
                        <TextField
                          required
                          fullWidth
                          label="First Name"
                          value={formData.firstname}
                          onChange={handleInputChange('firstname')}
                          error={!!errors.firstname}
                          helperText= 
                          {
                            errors.firstname ? 
                              errors.firstname.map((err) => (
                                <List>{err}</List>
                              ))
                            :
                              null
                          }
                          sx={ textFieldStyles }
                        />
                    </Stack>
                    <Stack sx={{ mt: 4 }}>
                        <TextField
                          required
                          fullWidth
                          label="Last Name"
                          value={formData.lastname}
                          onChange={handleInputChange('lastname')}
                          error={!!errors.lastname}
                          helperText= 
                          {
                            errors.lastname ? 
                              errors.lastname.map((err) => (
                                <List>{err}</List>
                              ))
                            :
                              null
                          }
                          sx={ textFieldStyles }
                      />
                  </Stack>
                  <Stack sx={{ mt: 4 }}>
                      <TextField
                        required
                        fullWidth
                        label="User Name"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                        error={!!errors.username}
                        helperText={errors.username}
                        sx={ textFieldStyles }
                      />
                  </Stack>
                  <Stack sx={{ mt: 4 }}>
                      <TextField
                        required  
                        fullWidth
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange('phone')}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        sx={ textFieldStyles }
                      />
                  </Stack>
                  <Stack sx={{ mt: 4 }}>
                      <TextField
                        required  
                        fullWidth
                        label="Email Address"
                        value={formData.emailaddr}
                        onChange={handleInputChange('emailaddr')}
                        error={!!errors.emailaddr}
                        helperText={errors.emailaddr}
                        sx={ textFieldStyles }
                      />
                  </Stack>
                  <Stack sx={{ mt: 4 }}>
                      <TextField
                        required
                        fullWidth
                        label="Password"
                        type={showPasswords.password ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        error={!!errors.password}
                        helperText= 
                        {
                          errors.password ? 
                            errors.password.map((err) => (
                              <List>{err}</List>
                            ))
                          :
                            null
                        }
                        sx={ textFieldStyles }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => togglePasswordVisibility('password')}
                                  edge="end"
                                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        }}
                      />
                  </Stack>
                  <Stack sx={{ mt: 4 }}>
                      <TextField
                        required
                        fullWidth
                        label="Confirm Password"
                        type={showPasswords.vPassword ? 'text' : 'password'}
                        value={formData.vPassword}
                        onChange={handleInputChange('vPassword')}
                        error={!!errors.vPassword}
                        helperText=
                        {
                            errors.vPassword ? 
                              errors.vPassword.map((err) => (
                                <List>{err}</List>
                              )) 
                              : 
                              null
                        }
                        sx={ textFieldStyles }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => togglePasswordVisibility('vPassword')}
                                  edge="end"
                                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  {showPasswords.vPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        }}
                      />
                  </Stack>
                  <ButtonGroup 
                    size="small"
                    variant="contained" 
                    aria-label="Basic button group" 
                    fullWidth
                    sx={{ pt: 2 }}
                    color="error"
                  >
                      <Button
                        onClick={handleClose}
                        type="cancel"
                        variant="contained"
                        sx={ Styles.buttonStyle }
                      >
                        Cancel
                      </Button>              
                      <Button
                        type="submit"                      
                        variant="contained"
                        disabled={isFormValid}
                        sx={{ ...Styles.buttonStyle,  ml:4 }}
                      >
                        Register User
                      </Button>
                  </ButtonGroup>
                </Box>
              </Box>        
          </Box>        
      </Modal>
    </>
  );
}